import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTemporaryStorage } from 'hooks/useTemporaryStorage';
import { CmsApiSubscribe } from 'modules/cms/api/types/CmsApiSubscribe';
import { showModal } from 'modules/main/store/ducks/modal.actions';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { getParam, isUrlWithProtocol, pathWithParams } from 'utils//url';
import { RichError } from 'utils/RichError';
import { postJson } from 'utils/http/postJson';
import { trackError } from 'utils/trackError';

type Props = {
  blockId: string;
  index: number;
  redirectUrl: string | null;
  render: (arg0: { subscribe: () => void; isSubscribed: boolean }) => ReactNode;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CmsSubscribeContainer(this: any, props: Props) {
  const dispatch = useMainDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserEnvironment();
  const tmpIdFromParams = getParam(location, 'tmpId');
  const [disabled, setDisabled] = useState(false);
  const [tmpId, setTmpId] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [saveTemporaryStorage, temporaryStorage] =
    useTemporaryStorage<CmsApiSubscribe>(
      Array.isArray(tmpIdFromParams) ? tmpIdFromParams[0] : tmpIdFromParams,
    );
  const redirectUrl =
    'redirectUrl' in temporaryStorage
      ? temporaryStorage.redirectUrl
      : // eslint-disable-next-line react/prop-types, react/destructuring-assignment
        props.redirectUrl;

  const subscribe = useCallback(() => {
    const showEmailSignupModal = (tmpIdToUse: string) => {
      const redirectTo = pathWithParams(
        `${window.location.pathname}${window.location.search}`,
        {
          tmpId: tmpIdToUse,
        },
      );
      dispatch(
        showModal('EMAIL_SIGNUP', {
          title: getText('Create an account on Idealist in order to subscribe'),
          redirectTo,
        }),
      );
    };

    const loggedInVerifiedWithInfo = Boolean(
      user &&
        user.email !== null &&
        user.emailVerified &&
        user.firstName &&
        user.lastName,
    );

    if (loggedInVerifiedWithInfo) {
      const postBaseUrl = '/data/website/button-link-campaign-signup/';
      // eslint-disable-next-line react/prop-types, react/destructuring-assignment
      postJson(postBaseUrl + props.blockId, {
        // eslint-disable-next-line react/prop-types, react/destructuring-assignment
        index: props.index,
      })
        .then(() => {
          if (typeof redirectUrl === 'string') {
            if (isUrlWithProtocol(redirectUrl)) {
              window.location.href = redirectUrl;
              return;
            }

            navigate(
              redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`,
            );
          } else {
            setIsSubscribed(true);
          }
        })
        .catch((e) => {
          trackError(
            // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
            new RichError('Error subscribing in CMS block', JSON.stringify(e)),
          );
        });
    } else {
      if (!disabled && !tmpId) {
        setDisabled(true);
        saveTemporaryStorage({
          CMS_SUBSCRIBE: {
            // eslint-disable-next-line react/prop-types, react/destructuring-assignment
            blockId: props.blockId,
            // eslint-disable-next-line react/prop-types, react/destructuring-assignment
            index: props.index,
            // eslint-disable-next-line react/prop-types, react/destructuring-assignment
            redirectUrl: props.redirectUrl,
          },
        }).then((createdTmpId) => {
          setDisabled(false);
          setTmpId(createdTmpId);
          return showEmailSignupModal(createdTmpId);
        });
      }

      showEmailSignupModal(tmpId);
    }
  }, [
    user,
    disabled,
    dispatch,
    navigate,
    // eslint-disable-next-line react/prop-types, react/destructuring-assignment
    props.blockId,
    // eslint-disable-next-line react/prop-types, react/destructuring-assignment
    props.index,
    // eslint-disable-next-line react/prop-types, react/destructuring-assignment
    props.redirectUrl,
    redirectUrl,
    saveTemporaryStorage,
    tmpId,
  ]);

  useEffect(() => {
    if (
      'blockId' in temporaryStorage &&
      // eslint-disable-next-line react/prop-types, react/destructuring-assignment
      temporaryStorage.blockId === props.blockId &&
      // eslint-disable-next-line react/prop-types, react/destructuring-assignment
      temporaryStorage.index === props.index &&
      user?.emailVerified
    ) {
      subscribe();
    }
  }, [
    user?.emailVerified,
    // eslint-disable-next-line react/prop-types, react/destructuring-assignment
    props.blockId,
    // eslint-disable-next-line react/prop-types, react/destructuring-assignment
    props.index,
    subscribe,
    temporaryStorage,
  ]);

  return (
    <>
      {
        // eslint-disable-next-line react/prop-types, react/destructuring-assignment
        props.render({
          subscribe: subscribe.bind(this),
          isSubscribed,
        })
      }
    </>
  );
}

// eslint-disable-next-line import/no-default-export
export default CmsSubscribeContainer;
